/**
 * OWA-998: task saves the server refused as stale, which this browser holds for the user
 * (plan steps 19 to 22).
 *
 * A held group is one refused save plus every queued save that continues it (they wait behind
 * it, because their content includes it). The user resolves a group in one of three ways:
 * - View the new task: open the editor on the task as it is now;
 * - Merge: TaskMerge between the group's newest version and the task as it is now; the result is
 *   queued from the current version and, once accepted, retires exactly that group's saves;
 * - Abandon: retire exactly that group's saves. The task itself is never deleted.
 */

import Task from '../entities/task';
import store from '../store/store';
import * as TaskActions from '../store/actions/taskActions';
import * as idb from '../lib/idbd';
import newHRTimestamp from '../lib/picos/timestamp';
import {getCurrentUserId} from '../lib/picos/currentLogin';
import {markFailed, type QueuedTask} from '../lib/taskLineage';
import {versionRef} from '../lib/staleSave';

export type HeldGroup = {
    /** The refused save. Its queue key (addedOn) identifies the group. */
    held: QueuedTask,
    /** The refused save and every queued save that continues it, oldest first. */
    rows: QueuedTask[],
    /** The newest version in the group: what the user last meant the task to be. */
    newest: QueuedTask,
    /** The task as it is now, from the refusal (it may be deleted), or null if it no longer exists. */
    current: Task | null,
    /** The version the edit began from, or null when that is not one known version. */
    base: Task | null,
};

const toTask = (value: unknown): Task | null =>
    value && typeof value === 'object' ? new Task({...(value as object), persisted: true}) : null;

/** The held groups among the queued task saves, oldest refusal first. */
export function heldGroups(rows: QueuedTask[]): HeldGroup[] {
    const childrenOf = new Map<string, QueuedTask[]>();
    for (const row of rows) {
        if (row.parentOp) childrenOf.set(row.parentOp, [...(childrenOf.get(row.parentOp) ?? []), row]);
    }
    const descendants = (root: QueuedTask): QueuedTask[] => {
        const found: QueuedTask[] = [];
        const visit = (row: QueuedTask) => {
            found.push(row);
            if (row.opId) (childrenOf.get(row.opId) ?? []).forEach(visit);
        };
        visit(root);
        return found;
    };

    const roots = rows.filter(row => row.conflict).sort((a, b) => a.addedOn - b.addedOn);
    const claimed = new Set<number>();
    const groups: HeldGroup[] = [];
    for (const held of roots) {
        // A refused save that continues another refused save belongs to that save's group
        if (claimed.has(held.addedOn)) continue;
        const members = descendants(held).sort((a, b) => a.addedOn - b.addedOn);
        members.forEach(row => claimed.add(row.addedOn));
        groups.push({
            held,
            rows: members,
            newest: members[members.length - 1],
            current: toTask(held.conflict?.current),
            base: toTask(held.conflict?.base),
        });
    }
    return groups;
}

/**
 * Follow the held groups. `onChange` receives the groups whenever the task queue changes.
 * The returned function stops following.
 */
export function observeHeldGroups(onChange: (groups: HeldGroup[]) => void): (() => void) | null {
    const observed = idb.observeTaskQueue(rows => onChange(heldGroups(rows as QueuedTask[])));
    if (observed.isErr()) {
        console.error('OWA-998: could not follow held task saves', observed.unwrapErr());
        return null;
    }
    return observed.unwrap();
}

/**
 * The queue keys a resolution of the group retires: its own saves, and the saves an earlier merge
 * in the group was going to retire (a merge can itself be refused and held).
 */
const resolvedKeys = (group: HeldGroup): number[] =>
    [...new Set(group.rows.flatMap(row => [row.addedOn, ...(row.resolves ?? [])]))];

/**
 * Abandon: retire exactly the group's saves. The task is not deleted; wherever the store still
 * shows one of the abandoned versions, it shows the task as it is now instead.
 */
export async function abandonHeldGroup(group: HeldGroup): Promise<boolean> {
    const retired = await idb.retireTasks(resolvedKeys(group));
    if (retired.isErr()) {
        console.error('OWA-998: could not abandon held task saves', retired.unwrapErr());
        return false;
    }
    // Already resolved (here, in another tab, or by a merge): nothing is left to abandon
    if (retired.unwrap() === 0) return true;
    group.rows.forEach(row => markFailed(row.opId));
    if (group.current) group.rows.forEach(row => store.dispatch(TaskActions.staleTask(group.current!, row.addedOn)));
    return true;
}

/**
 * Abandon the group whose refused save is `heldAddedOn`, as the queue holds it now (saves may have
 * joined it since it was shown). True when nothing of it is left, including when it was already
 * resolved.
 */
export async function abandonHeldGroupNow(heldAddedOn: number): Promise<boolean> {
    const rows = await idb.getSingleStore('task');
    if (rows.isErr()) {
        console.error('OWA-998: could not read held task saves', rows.unwrapErr());
        return false;
    }
    const group = heldGroups(rows.unwrap() as QueuedTask[]).find(g => g.held.addedOn === heldAddedOn);
    return group ? abandonHeldGroup(group) : true;
}

/**
 * Merge: queue `merged` (built from the group's newest version and the task as it is now) as an
 * edit of the current version. When the server accepts it, the group's saves are retired with it.
 * When the current version is a deletion, the merge restores the task with the user's changes.
 */
export function saveMergedGroup(group: HeldGroup, merged: Task): void {
    const current = group.current;
    if (!current) throw new Error('A held task save cannot be merged without the task as it is now');

    const save = merged.clone() as QueuedTask;
    save.baseVersion = versionRef(current as Task & {addedOn: number}) as Task['baseVersion'];
    save.pendingOp = null;
    save.resolves = resolvedKeys(group);
    save.addedOn = newHRTimestamp();
    save.addedBy = getCurrentUserId();

    if (current.status === 'D') store.dispatch(TaskActions.restoreTaskQueue(save));
    else store.dispatch(TaskActions.saveTaskQueue(save));
}
